// frontend/src/pages/Tickets/OrganizationTickets.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Space, Button, Input, Typography, Card, Spin, Modal, Tooltip } from 'antd';
import {
  SearchOutlined, DownloadOutlined, EyeOutlined, FilterOutlined,
  CheckCircleOutlined, CloseCircleOutlined, TeamOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../utils/api';
import './TicketsList.css'; // Reuse the same CSS
import { useNavigate } from 'react-router-dom';
import { useNotificationApi } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { Search } = Input;

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'unassigned': return 'red';
    case 'open': return 'blue';
    case 'waiting for support': return 'orange';
    case 'in progress': return 'geekblue';
    case 'resolved': return 'green';
    case 'closed': return 'default';
    case 'pending approval': return 'purple';
    case 're-opened': return 'volcano';
    case 'on-hold': return 'magenta';
    case 'cancelled': return 'red';
    default: return 'default';
  }
};

// Helper to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'urgent': return 'red';
    case 'high': return 'volcano';
    case 'medium': return 'gold';
    case 'low': return 'lime';
    case 'critical': return 'red';
    default: return 'default';
  }
};

const OrganizationTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortInfo, setSortInfo] = useState({});
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  const { user } = useAuth(); // Get current user info
  // State to store application settings
  const [appSettings, setAppSettings] = useState({
    dateFormat: 'DD/MM/YYYY', // Default if not fetched
    timeFormat: 'HH:mm',     // Default if not fetched
    appTitle: 'My Application' // Default
  });


  // --- Notification Helper Functions (standardized from TicketCategories.js) ---
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);
  // --- End Notification Helper Functions ---

  // Function to fetch application settings
  const fetchAppSettings = useCallback(async () => {
    try {
      const response = await api.get('/api/settings'); // Call the settings API
      if (response.data && response.data.success) {
        setAppSettings(prev => ({
          ...prev, // Keep existing defaults
          ...response.data.data // Overwrite with fetched settings
        }));
      }
    } catch (error) {
      console.error('Error fetching application settings:', error.response?.data || error.message);
      // Fallback to default date/time formats if fetch fails
    }
  }, []);


  // Function to fetch organization tickets from the backend
  const fetchTickets = useCallback(async (params = {}, triggeredByAction = false) => {
    if (!user || !user.id) {
      setLoadingTickets(false);
      return;
    }

    setLoadingTickets(true);
    const loadingKey = 'fetchOrganizationTicketsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading organization tickets...');

    try {
        const response = await api.get('/api/tickets/organization-tickets', {
            params: {
                search: globalSearchText,
                status: columnFilters?.statusName?.join(','),
                priority: columnFilters?.priorityName?.join(','),
                category: columnFilters?.ticketCategoryName?.join(','),
                sortBy: sortInfo?.field ? 
                    `${sortInfo.field}:${sortInfo.order === 'ascend' ? 'ASC' : 'DESC'}` : 
                    undefined,
            },
        });

      const responseData = response.data.data;
      const totalCount = response.data.totalCount;

      setTickets(Array.isArray(responseData) ? responseData : []);
      setPagination(prev => ({
        ...prev,
        total: totalCount || 0,
        current: params.pagination?.current || prev.current,
        pageSize: params.pagination?.pageSize || prev.pageSize,
      }));
      hideLoading();
      if (!triggeredByAction) {
        showSuccess('Tickets Loaded', 'Organization ticket list fetched successfully!', 'fetchTicketsSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching organization tickets:', error.response?.data || error.message, error);
      showError('Failed to Fetch Organization Tickets', error.response?.data?.message || 'An unexpected error occurred while fetching organization tickets.', 'fetchTicketsError');
      setTickets([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoadingTickets(false);
    }
  }, [globalSearchText, notificationApi, showError, showSuccess, showLoading, user]);

  // Initial fetch on component mount and when filters/pagination change
  useEffect(() => {
    fetchTickets({
      pagination: { current: pagination.current, pageSize: pagination.pageSize },
      globalSearchText,
      columnFilters,
      sortInfo
    });
    // Fetch app settings when component mounts
    fetchAppSettings();
  }, [fetchTickets, globalSearchText, columnFilters, sortInfo, fetchAppSettings]);

  // Handles table changes (pagination, sorting, filtering)
  const handleTableChange = (newPagination, filters, sorter, extra) => {
    const mappedFilters = {};
    for (const key in filters) {
        if (filters[key] && filters[key].length > 0) {
            const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            mappedFilters[camelCaseKey] = filters[key];
        }
    }
    setColumnFilters(mappedFilters);

    if (sorter.field) {
        setSortInfo({ field: sorter.field, order: sorter.order });
    } else {
        setSortInfo({});
    }

    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  // Handler for global search input
  const handleGlobalSearch = (value) => {
    setGlobalSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  // Handle Download PDF
  const handleDownloadPdf = async (ticketUuid) => {
    const loadingKey = 'downloadPdfLoading';
    const hideLoading = showLoading(loadingKey, 'Generating PDF...');

    try {
        const response = await api.get(`/api/reports/tickets/${ticketUuid}/borang-aduan`, {
            params: { format: 'pdf' },
            responseType: 'blob',
            timeout: 60000, // Increased timeout to 60 seconds (60000ms)
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename = `BorangAduan_${ticketUuid}.pdf`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8''|\w*')?([^"'\r\n]+)['"]?/);
            if (filenameMatch && filenameMatch[1]) {
                filename = decodeURIComponent(filenameMatch[1]);
            }
        }

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        hideLoading();
        showSuccess('PDF Generated', `Borang Aduan for ticket ${ticketUuid} downloaded successfully!`, 'downloadPdfSuccess');
    } catch (error) {
        hideLoading();
        console.error('Error downloading PDF:', error.response?.data || error.message, error);
        let errorMessage = 'An unexpected error occurred while generating the PDF.';
        if (error.response && error.response.data instanceof Blob) {
            const errorText = await error.response.data.text();
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.message || errorMessage;
            } catch (parseError) {
                errorMessage = errorText || errorMessage;
            }
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        }

        showError('Failed to Download PDF', errorMessage, 'downloadPdfError');
    }
  };


  // Helper for text search columns
  const getColumnSearchProps = (dataIndex, placeholderText) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${placeholderText}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  const columns = [
    {
      title: 'Number',
      dataIndex: 'ticketUuid',
      key: 'ticketUuid',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewTicket(record.id)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
      ...getColumnSearchProps('ticketUuid', 'Number'),
      sorter: true,
      fixed: 'left',
      width: 180,
    },
    {
      title: 'Summary',
      dataIndex: 'title',
      key: 'summary',
      ...getColumnSearchProps('title', 'Summary'),
      sorter: true,
      ellipsis: true,
      width: 300,
    },
    {
      title: 'Reporter',
      dataIndex: 'creatorFirstName',
      key: 'reporter',
      render: (text, record) => `${record.creatorFirstName || ''} ${record.creatorLastName || ''}`,
      ...getColumnSearchProps('creatorFirstName', 'Reporter'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeFirstName',
      key: 'assignee',
      render: (text, record) => record.assigneeFirstName ? `${record.assigneeFirstName || ''} ${record.assigneeLastName || ''}` : 'Unassigned',
      ...getColumnSearchProps('assigneeFirstName', 'Assignee'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
      ...getColumnSearchProps('organizationName', 'Organization'),
      sorter: true,
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'statusName',
      filters: [
        { text: 'Unassigned', value: 'Unassigned' },
        { text: 'Open', value: 'Open' },
        { text: 'Waiting for Support', value: 'Waiting for Support' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Resolved', value: 'Resolved' },
        { text: 'Closed', value: 'Closed' },
        { text: 'Pending Approval', value: 'Pending Approval' },
        { text: 'Re-opened', value: 'Re-opened' },
        { text: 'On-Hold', value: 'On-Hold' },
        { text: 'Cancelled', value: 'Cancelled' },
      ],
      onFilter: (value, record) => record.statusName?.toLowerCase().includes(value.toLowerCase()),
      render: (status) => (
        <Tag color={getStatusColor(status)} key={status}>
          {status?.toUpperCase()}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'Priority',
      dataIndex: 'priorityName',
      key: 'priorityName',
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
      ],
      onFilter: (value, record) => record.priorityName?.toLowerCase().includes(value.toLowerCase()),
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} key={priority}>
          {priority?.toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Reported Date',
      dataIndex: 'caseReportedDate',
      key: 'caseReportedDate',
      sorter: true,
      // Use appSettings.dateFormat and appSettings.timeFormat
      render: (text) => text ? moment(text).format(`${appSettings.dateFormat} ${appSettings.timeFormat}`) : 'N/A',
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewTicket(record.id)}>
            View
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPdf(record.ticketUuid)}
          >
            Download PDF
          </Button>
        </Space>
      ),
      width: 180,
      fixed: 'right',
    },
  ];

  return (
    <Card className="tickets-list-page-card">
      <div className="tickets-list-header">
        <Title level={2} className="tickets-list-title">
          <TeamOutlined style={{ marginRight: '8px' }} />
          Organization Tickets
        </Title>
        <Text type="secondary" className="tickets-list-description">
          View and manage tickets from your organization.
        </Text>
      </div>

      <div className="tickets-table-filters-container mb-4">
        <Search
          placeholder="Search by ticket number, summary, reporter, assignee, or organization..."
          enterButton
          value={globalSearchText}
          onChange={(e) => setGlobalSearchText(e.target.value)}
          onSearch={handleGlobalSearch}
          style={{ width: 400 }}
          className="tickets-global-search"
        />
      </div>

      <div className="tickets-table-container">
        <Table
          columns={columns}
          dataSource={tickets}
          rowKey="id"
          pagination={pagination}
          loading={loadingTickets}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          className="tickets-ant-table"
        />
      </div>
    </Card>
  );
};

export default OrganizationTickets;